/**
 * Email template definitions for automated (transactional) notifications.
 *
 * Each event has a stable `key` (stored on notification_templates.event_key),
 * a human label, the list of dynamic variables an organizer can use, and a
 * built-in default subject/message. When a conference has no custom template
 * for an event, the default is used — so emails always send.
 *
 * Messages are plain text. The send-email edge function wraps them in the
 * branded HTML shell and converts newlines to <br>, so \n is fine here.
 */

export const EMAIL_EVENTS = [
  {
    key: 'submission_received',
    label: 'Submission received',
    description: 'Sent to the author when they submit a paper.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the submitted paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'participantId', description: 'Author conference ID (if assigned)' }
    ],
    defaultSubject: 'Paper Submission Confirmation - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Thank you for submitting your paper to {{conferenceName}}.

Paper Title: {{paperTitle}}
Your Conference ID: {{participantId}}

Your submission has been received and will be reviewed by our program committee. You will be notified once the review process is complete.

You can track the status of your submission from your dashboard.

Best regards,
{{conferenceName}} Team`
  },
  {
    key: 'paper_accepted',
    label: 'Paper accepted',
    description: 'Sent to the author when their paper is accepted.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' }
    ],
    defaultSubject: 'Paper Decision: Accepted - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

We are pleased to inform you that your paper titled "{{paperTitle}}", submitted to {{conferenceName}}, has been accepted. Congratulations on this achievement!

Please log in to your dashboard for the next steps, including any camera-ready and registration instructions.

We appreciate your contribution and look forward to your participation.

Best regards,
{{conferenceName}} Program Committee`
  },
  {
    key: 'paper_accepted_minor_revision',
    label: 'Paper accepted — minor revisions',
    description:
      'Sent when a paper is accepted on condition of minor changes. The paper is in; the changes are a condition of the camera-ready.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'revisionNote', description: 'What the chair asked to be changed' },
      { name: 'cameraReadyDeadline', description: 'Camera-ready deadline, if set' }
    ],
    defaultSubject: 'Paper Decision: Accepted with minor revisions - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Your paper "{{paperTitle}}" has been accepted for {{conferenceName}}, subject to minor revisions. Congratulations.

The committee asks that you address the following before submitting your camera-ready version:

{{revisionNote}}

You do not need to resubmit for another round of review — make the changes and upload your camera-ready by {{cameraReadyDeadline}}.

Best regards,
{{conferenceName}} Program Committee`
  },
  {
    key: 'paper_accepted_major_revision',
    label: 'Paper accepted — major revisions',
    description:
      'Sent when a paper is accepted on condition of substantial changes.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'revisionNote', description: 'What the chair asked to be changed' },
      { name: 'cameraReadyDeadline', description: 'Camera-ready deadline, if set' }
    ],
    defaultSubject: 'Paper Decision: Accepted with major revisions - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Your paper "{{paperTitle}}" has been accepted for {{conferenceName}}, subject to major revisions. Congratulations.

The committee considers the contribution sound but expects substantial changes before publication:

{{revisionNote}}

Please treat these as required rather than optional. Upload your revised camera-ready version by {{cameraReadyDeadline}}.

If any of this is unclear, reply to this message and the committee will clarify.

Best regards,
{{conferenceName}} Program Committee`
  },
  {
    key: 'paper_rejected',
    label: 'Paper rejected',
    description: 'Sent to the author when their paper is rejected.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' }
    ],
    defaultSubject: 'Paper Decision: Not Accepted - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Thank you for submitting your paper titled "{{paperTitle}}" to {{conferenceName}}.

After careful review by our program committee, we regret to inform you that your paper has not been accepted for this edition. We received many strong submissions and could not accommodate them all.

You can view the reviewer feedback on your dashboard. We hope you will consider submitting to future editions.

Best regards,
{{conferenceName}} Program Committee`
  },
  {
    key: 'acceptance_withdrawn',
    label: 'Acceptance withdrawn',
    description:
      'Sent when a paper accepted conditional on revisions is later withdrawn because the required changes were not reflected in the camera-ready. Distinct from "Paper rejected", which is a first-time review outcome — this one references the earlier acceptance instead of re-explaining the review process.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'revisionNote', description: 'What the chair had originally asked to be changed' }
    ],
    defaultSubject: 'Paper Decision: Acceptance Withdrawn - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Your paper "{{paperTitle}}" was previously accepted for {{conferenceName}} on condition of revisions:

{{revisionNote}}

Having reviewed your camera-ready submission, the committee has found that these changes were not sufficiently addressed. As a result, we must withdraw the acceptance of this paper — it will not be included in the program or proceedings.

If you believe this decision was made in error, please reply to this message.

Best regards,
{{conferenceName}} Program Committee`
  },
  {
    key: 'revision_required',
    label: 'Revision required',
    description: 'Sent to the author when a revision is requested.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'decision', description: 'Decision label (e.g. Minor / Major Revisions)' }
    ],
    defaultSubject: 'Paper Decision: {{decision}} - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Your paper titled "{{paperTitle}}", submitted to {{conferenceName}}, requires revisions before a final decision can be made.

Decision: {{decision}}

Please log in to your dashboard to view the reviewer comments and submit your revised version.

Best regards,
{{conferenceName}} Program Committee`
  },
  {
    key: 'reviewer_assignment',
    label: 'Reviewer assignment',
    description: 'Sent to a reviewer when assigned a paper to review.',
    recipient: 'Reviewer',
    variables: [
      { name: 'reviewerName', description: "Reviewer's full name" },
      { name: 'paperTitle', description: 'Title of the assigned paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'reviewLink', description: 'Link to the reviewer dashboard' }
    ],
    defaultSubject: 'New Paper Assignment - {{conferenceName}}',
    defaultMessage:
`Dear {{reviewerName}},

You have been assigned to review the following paper for {{conferenceName}}:

Title: {{paperTitle}}

Please visit your Reviewer Dashboard to view the full paper details and submit your review:
{{reviewLink}}

Thank you for your contribution to the conference review process.

Best regards,
{{conferenceName}} Team`
  },
  {
    key: 'review_submitted',
    label: 'Review submitted',
    description: 'Confirmation sent to a reviewer after they submit a review.',
    recipient: 'Reviewer',
    variables: [
      { name: 'reviewerName', description: "Reviewer's full name" },
      { name: 'paperTitle', description: 'Title of the reviewed paper' },
      { name: 'conferenceName', description: 'Conference name' },
      { name: 'reviewLink', description: 'Link to the reviewer dashboard' }
    ],
    defaultSubject: 'Review Submitted Successfully - {{conferenceName}}',
    defaultMessage:
`Dear {{reviewerName}},

Your review has been successfully submitted for the following paper:

Title: {{paperTitle}}
Conference: {{conferenceName}}

Thank you for your valuable contribution to the review process. You can view your submitted reviews on your Reviewer Dashboard:
{{reviewLink}}

Best regards,
{{conferenceName}} Team`
  },
  {
    key: 'camera_ready_received',
    label: 'Camera-ready received',
    description: 'Confirmation sent to the author after a camera-ready upload.',
    recipient: 'Author',
    variables: [
      { name: 'authorName', description: "Author's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'conferenceName', description: 'Conference name' }
    ],
    defaultSubject: 'Camera-Ready Submission Confirmed - {{conferenceName}}',
    defaultMessage:
`Dear {{authorName}},

Your camera-ready version has been successfully submitted for:

Title: {{paperTitle}}
Conference: {{conferenceName}}

No further action is required. Thank you for your submission.

Best regards,
{{conferenceName}} Team`
  },
  {
    key: 'camera_ready_chair',
    label: 'Camera-ready received (chair notice)',
    description: 'Sent to conference chairs when an author uploads a camera-ready version.',
    recipient: 'Chair',
    variables: [
      { name: 'chairName', description: "Chair's full name" },
      { name: 'paperTitle', description: 'Title of the paper' },
      { name: 'authorName', description: "Author's full name" },
      { name: 'conferenceName', description: 'Conference name' }
    ],
    defaultSubject: 'Camera-Ready Received: {{paperTitle}} - {{conferenceName}}',
    defaultMessage:
`Dear {{chairName}},

A camera-ready version has been submitted:

Paper: {{paperTitle}}
Author: {{authorName}}
Conference: {{conferenceName}}

You can view the submission in your conference management dashboard.

Best regards,
{{conferenceName}} Team`
  }
];

/** Look up an event definition by its key. */
export const getEmailEvent = (eventKey) =>
  EMAIL_EVENTS?.find((event) => event?.key === eventKey) || null;

/**
 * Substitute {{variable}} placeholders in a string with values from `vars`.
 * Unknown or empty variables are replaced with an empty string so stray
 * placeholders never reach the recipient.
 */
export const substituteVariables = (text, vars = {}) => {
  if (!text) return '';
  return text?.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
    const value = vars?.[name];
    return value === undefined || value === null ? '' : String(value);
  });
};

/**
 * Render an event email. Uses the provided custom template (subject/message)
 * when present, otherwise the built-in default for the event, then applies
 * variable substitution.
 *
 * Returns { subject, message } or null if the event key is unknown.
 */
export const renderEmailEvent = (eventKey, customTemplate, vars = {}) => {
  const event = getEmailEvent(eventKey);
  if (!event) return null;

  const subjectTemplate = customTemplate?.subject?.trim()
    ? customTemplate?.subject
    : event?.defaultSubject;
  const messageTemplate = customTemplate?.message?.trim()
    ? customTemplate?.message
    : event?.defaultMessage;

  return {
    subject: substituteVariables(subjectTemplate, vars),
    message: substituteVariables(messageTemplate, vars)
  };
};

export default EMAIL_EVENTS;
